import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "./styles/App.css";
import Header from "./components/Header";
import Gallery from "./components/Gallery";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import { TRANSLATIONS } from "./data/translations";

const ImageCarousel = React.memo(() => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = [
     { src: "/bed.webp", alt: "Chambre" },
     { src: "/kitchen.webp", alt: "Cuisine" },
    { src: "/tv.webp", alt: "Television" },
    { src: "/bathroom.webp", alt: "Salle de bain" },
    { src: "/office.webp", alt: "Bureau" },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  return (
    <div className="carousel-container">
      <div className="carousel-wrapper">
        {images.map((image, index) => (
          <img
            key={index}
            src={image.src}
            alt={image.alt}
            className={`carousel-image ${
              index === currentIndex ? "active" : ""
            }`}
            loading="lazy"
          />
        ))}
      </div>

      <button
        className="carousel-button carousel-button--prev"
        onClick={goToPrevious}
        aria-label="Previous image"
      >
        ←
      </button>

      <button
        className="carousel-button carousel-button--next"
        onClick={goToNext}
        aria-label="Next image"
      >
        →
      </button>

      <div className="carousel-indicators">
        {images.map((_, index) => (
          <button
            key={index}
            className={`carousel-dot ${index === currentIndex ? "active" : ""}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
});

const HomePage = ({ language }) => {
  const t = React.useMemo(() => TRANSLATIONS[language], [language]);

  // Charger le script Lodgify
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://app.lodgify.com/book-now-box/stable/renderBookNowBox.js";
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const getLocationIcon = (type) => {
    const icons = {
      building: "🏢",
      train: "🚂",
      car: "🚗",
    };
    return icons[type] || icons.building;
  };

  return (
    <main className="main-content">
      <div className="hero-section">
        <div className="carousel-section">
          <ImageCarousel />
        </div>

        <div className="hero-text-section">
          <h1 className="hero-title">{t.content.subtitle}</h1>
          <ul className="hero-features-list">
            {t.content.features.map((feature, index) => (
              <li key={index} className="hero-feature-item">
                {feature}
              </li>
            ))}
          </ul>
          <p className="hero-description">{t.content.description1}</p>
        </div>
      </div>

      <div className="content-container">
        <div className="text-content">
          <p className="description">{t.content.description2}</p>

          <div className="location-section">
            <h3 className="location-title">{t.content.locationTitle}</h3>
            <p className="location-intro">{t.content.locationIntro}</p>

            <div className="location-grid">
              {t.content.locations.map((location, index) => (
                <div key={index} className="location-item">
                  <div className="location-icon">
                    {getLocationIcon(location.icon)}
                  </div>
                  <h4 className="location-name">{location.name}</h4>
                  <p className="location-distance">{location.distance}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Widget de réservation Lodgify */}
          <div className="booking-widget-section">
            <style
              dangerouslySetInnerHTML={{
                __html: `
                :root {
                  --ldg-bnb-background: #ffffff;
                  --ldg-bnb-border-radius: 0;
                  --ldg-bnb-box-shadow: 0px 24px 54px 0px rgba(0, 0, 0, 0.1);
                  --ldg-bnb-padding: 14px;
                  --ldg-bnb-input-background: #ffffff;
                  --ldg-bnb-button-border-radius: 0;
                  --ldg-bnb-color-primary: #2c2c2c;
                  --ldg-bnb-color-primary-lighter: #5a5a5a;
                  --ldg-bnb-color-primary-darker: #1a1a1a;
                  --ldg-bnb-color-primary-contrast: #ffffff;
                  --ldg-component-calendar-cell-selection-bg-color: #2c2c2c;
                  --ldg-component-calendar-cell-selection-color: #ffffff;
                  --ldg-component-calendar-cell-selected-bg-color: #5a5a5a;
                  --ldg-component-calendar-cell-selected-color: #ffffff;
                  --ldg-bnb-font-family: "Helvetica Neue", Arial, sans-serif;
                }
                #lodgify-book-now-box {    
                  width: 100%;
                  border: 1px solid #f0f0f0;
                  border-radius: 0;
                }
              `,
              }}
            />

            <div
              id="lodgify-book-now-box"
              data-rental-id="700978"
              data-website-id="601073"
              data-slug="juralodgespa"
              data-language-code={language}
              data-new-tab="true"
              data-version="stable"
              data-currency-code="CHF"
              data-check-in-label={
                language === "fr"
                  ? "Arrivée"
                  : language === "en"
                    ? "Check-in"
                    : "Anreise"
              }
              data-check-out-label={
                language === "fr"
                  ? "Départ"
                  : language === "en"
                    ? "Check-out"
                    : "Abreise"
              }
              data-guests-label={
                language === "fr"
                  ? "Invités"
                  : language === "en"
                    ? "Guests"
                    : "Gäste"
              }
              data-guests-singular-label={
                language === "fr"
                  ? "{{NumberOfGuests}} invité"
                  : language === "en"
                    ? "{{NumberOfGuests}} guest"
                    : "{{NumberOfGuests}} Gast"
              }
              data-guests-plural-label={
                language === "fr"
                  ? "{{NumberOfGuests}} invités"
                  : language === "en"
                    ? "{{NumberOfGuests}} guests"
                    : "{{NumberOfGuests}} Gäste"
              }
              data-book-button-label={
                language === "fr"
                  ? "Réservez maintenant"
                  : language === "en"
                    ? "Book now"
                    : "Jetzt buchen"
              }
              data-total-price-label={
                language === "fr"
                  ? "Prix total :"
                  : language === "en"
                    ? "Total price:"
                    : "Gesamtpreis:"
              }
              data-select-dates-to-see-price-label={
                language === "fr"
                  ? "Sélectionnez les dates pour voir le prix total"
                  : language === "en"
                    ? "Select dates to see total price"
                    : "Daten auswählen um Gesamtpreis zu sehen"
              }
              data-minimum-price-per-night-first-label={
                language === "fr"
                  ? "À partir de"
                  : language === "en"
                    ? "From"
                    : "Ab"
              }
              data-minimum-price-per-night-second-label={
                language === "fr"
                  ? "par nuit"
                  : language === "en"
                    ? "per night"
                    : "pro Nacht"
              }
            />
          </div>
        </div>
      </div>
    </main>
  );
};

const App = () => {
  const [currentLang, setCurrentLang] = React.useState("fr");

  React.useEffect(() => {
    const titles = {
      fr: "Studio Delémont - Votre Appart-hôtel dans l'écoquartier de Delémont",
      en: "Studio Delémont - A peaceful retreat in a contemporary, eco-conscious setting",
      de: "Studio Delémont - Eine Ruheoase in zeitgemäßer, umweltbewusster Atmosphäre",
    };
    document.title = titles[currentLang] || titles.fr;
  }, [currentLang]);

  const whatsappMessages = {
    fr: "Bonjour ! Je souhaite avoir des informations sur le Studio Delémont pour une réservation.",
    en: "Hello! I would like information about Studio Delémont for a booking.",
    de: "Hallo! Ich hätte gerne Informationen über das Studio Delémont für eine Buchung.",
  };

  return (
    <Router>
      <div className="app">
        <Header language={currentLang} onLanguageChange={setCurrentLang} />
        <Routes>
          <Route path="/" element={<HomePage language={currentLang} />} />
          <Route path="/gallery" element={<Gallery language={currentLang} />} />
        </Routes>
        <Footer language={currentLang} />

        <WhatsAppButton
          phoneNumber="41786304539"
          message={whatsappMessages[currentLang] || whatsappMessages.fr}
        />
      </div>
    </Router>
  );
};

export default App;
